import { Home, Car, ClipboardList, User, LogOut, Wrench } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  /* 4 balanced nav items — same for logged-in and logged-out, 
     just different destinations for auth-gated items */
  const leftItems = [
    { icon: Home, label: "Home", path: "/" },
    { icon: Car, label: "Vehicles", path: user ? "/dashboard/vehicles" : "/login" },
  ];
  const rightItems = [
    { icon: ClipboardList, label: "History", path: user ? "/dashboard/history" : "/login" },
    user
      ? { icon: LogOut, label: "Logout", action: () => { logout(); navigate("/"); } }
      : { icon: User, label: "Account", path: "/login" },
  ];

  const handleNav = (it) => {
    if (it.action) return it.action();
    navigate(it.path);
  };

  const handleFAB = () => {
    const el = document.querySelector("#booking");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/", { state: { scrollTo: "#booking" } });
  };

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const active = item.path && isActive(item.path);
    return (
      <button
        onClick={() => handleNav(item)}
        className={`haptic relative flex flex-col items-center justify-center gap-0.5 flex-1 min-h-[56px] transition-colors duration-200 ${
          active
            ? isDark ? "text-[#00F0FF]" : "text-brand-blue"
            : isDark ? "text-white/50" : "text-slate-400"
        }`}
      >
        <Icon size={22} strokeWidth={active ? 2.2 : 1.6} />
        <span className={`text-[10px] tracking-wide ${active ? "font-bold" : "font-medium"}`}>
          {item.label}
        </span>
        {/* Active dot */}
        {active && (
          <span className={`absolute top-1.5 w-1 h-1 rounded-full ${isDark ? "bg-[#00F0FF]" : "bg-brand-blue"}`} />
        )}
      </button>
    );
  };

  return (
    <nav
      className="bottom-nav bottom-nav-safe fixed bottom-0 left-0 right-0 z-50 md:hidden"
      style={{
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      <div className="relative flex items-stretch">
        {/* Left 2 items */}
        {leftItems.map((it) => <NavItem key={it.label} item={it} />)}

        {/* Center spacer for FAB */}
        <div className="w-[72px] flex-shrink-0" />

        {/* Right 2 items */}
        {rightItems.map((it) => <NavItem key={it.label} item={it} />)}

        {/* Centered FAB — Book Now */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-6">
          <button
            onClick={handleFAB}
            className={`haptic group flex items-center justify-center w-14 h-14 rounded-2xl shadow-xl transition-all duration-200 active:scale-90 ${
              isDark
                ? "bg-[#00F0FF] text-black shadow-[0_4px_24px_rgba(0,240,255,0.45)]"
                : "bg-gradient-to-br from-brand-orange to-brand-orange-dark text-white shadow-[0_4px_24px_rgba(249,115,22,0.45)]"
            }`}
            aria-label="Book Now"
          >
            <Wrench size={24} strokeWidth={2.4} className="group-active:rotate-12 transition-transform duration-150" />
          </button>
          <span className={`block text-center text-[9px] font-bold tracking-wider mt-1 ${isDark ? "text-[#00F0FF]" : "text-brand-orange"}`}>
            BOOK
          </span>
        </div>
      </div>
    </nav>
  );
}
